/**
 * EAN / UPC Barcode Validator and Checksum Calculator.
 * Supports standard retail formats:
 * - EAN-8 (8 digits)
 * - UPC-A / EAN-12 (12 digits)
 * - EAN-13 (13 digits)
 */

import type { Language } from "../i18n";
import { t } from "../i18n";

export type BarcodeType = "EAN-8" | "UPC-A" | "EAN-13";

export interface BarcodeValidationResult {
  isValid: boolean;
  normalized: string;
  error?: string;
  barcodeType?: BarcodeType;
  expectedCheckDigit?: number;
}

/**
 * Calculates the standard GS1 modulo-10 check digit for a string of digits (without check digit).
 * The algorithm alternates multipliers 3 and 1 starting from the rightmost digit of the payload (weight 3).
 *
 * @param payload - String of digits without the final check digit.
 * @returns Modulo-10 check digit (0-9).
 */
export function calculateEanCheckDigit(payload: string): number {
  if (!/^\d+$/.test(payload)) {
    throw new Error("Payload must contain only digits");
  }

  let sum = 0;
  let weight = 3;

  for (let i = payload.length - 1; i >= 0; i--) {
    const digit = parseInt(payload[i], 10);
    sum += digit * weight;
    weight = weight === 3 ? 1 : 3;
  }

  const remainder = sum % 10;
  return remainder === 0 ? 0 : 10 - remainder;
}

/**
 * Validates a barcode string:
 * 1. Cleans spaces and hyphens.
 * 2. Checks length (8 for EAN-8, 12 for UPC-A, 13 for EAN-13).
 * 3. Verifies modulo-10 check digit.
 */
export function validateBarcode(raw: string, lang: Language = "ru"): BarcodeValidationResult {
  if (!raw || typeof raw !== "string") {
    return { isValid: false, normalized: "", error: t(lang, "val_empty") };
  }

  const cleaned = raw.replace(/[\s-]/g, "");

  if (cleaned.length === 0) {
    return { isValid: false, normalized: "", error: t(lang, "val_empty") };
  }

  if (!/^\d+$/.test(cleaned)) {
    return {
      isValid: false,
      normalized: cleaned,
      error: t(lang, "val_digits_only"),
    };
  }

  const len = cleaned.length;
  let barcodeType: BarcodeType;

  if (len === 8) {
    barcodeType = "EAN-8";
  } else if (len === 12) {
    barcodeType = "UPC-A";
  } else if (len === 13) {
    barcodeType = "EAN-13";
  } else {
    return {
      isValid: false,
      normalized: cleaned,
      error: t(lang, "val_invalid_length"),
    };
  }

  const payload = cleaned.slice(0, -1);
  const actualCheckDigit = parseInt(cleaned[cleaned.length - 1], 10);
  const expectedCheckDigit = calculateEanCheckDigit(payload);

  if (actualCheckDigit !== expectedCheckDigit) {
    return {
      isValid: false,
      normalized: cleaned,
      error: t(lang, "val_checksum_error", { expected: expectedCheckDigit }),
      barcodeType,
      expectedCheckDigit,
    };
  }

  return {
    isValid: true,
    normalized: cleaned,
    barcodeType,
    expectedCheckDigit,
  };
}

